import { useContext, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import dayjs from 'dayjs';

import { featuresContext } from '../lib/context';

const MAX_RANGES = 5;

const createRange = (start: any = null, end: any = null) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    start,
    end,
});

const getMinutes = (value: any) => {
    if (!value || !dayjs(value).isValid()) {
        return null;
    }
    return dayjs(value).hour() * 60 + dayjs(value).minute();
};

function WorkingTimeRangesEditor({
    resetKey,
    initialRanges = [],
    offsetString,
    disabled = false,
    onRangesChange = () => {},
}: any) {
    const features = useContext(featuresContext);
    const canAddMultipleRanges = features.includes('multiple_alert_period');

    const buildInitialRanges = (ranges: any) => {
        if (!ranges || ranges.length === 0) return [createRange(null, null)];
        return ranges.map((r: any) => createRange(r.start ?? null, r.end ?? null));
    };

    const [ranges, setRanges] = useState(() => buildInitialRanges(initialRanges));

    useEffect(() => {
        setRanges(buildInitialRanges(initialRanges));
    }, [resetKey]);

    useEffect(() => {
        onRangesChange(ranges.map((r: any) => ({ start: r.start ?? null, end: r.end ?? null })));
    }, [ranges, onRangesChange]);

    const validation = useMemo(() => {
        const invalidIds = new Set();
        const messages = [];
        const normalizedRanges = ranges
            .map((range: any) => {
                const startMinutes = getMinutes(range.start);
                const endMinutes = getMinutes(range.end);
                // If end <= start, treat as cross-day (e.g. 22:00 - 02:00 → 22:00 - 26:00)
                const adjustedEndMinutes =
                    startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes
                        ? endMinutes + 24 * 60
                        : endMinutes;
                return {
                    ...range,
                    startMinutes,
                    endMinutes: adjustedEndMinutes,
                    isCrossDay: startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes,
                };
            })
            .filter((range: any) => range.startMinutes !== null && range.endMinutes !== null);

        // No invalid single-range check needed — cross-day is now allowed (end wraps past midnight)

        const comparableRanges = normalizedRanges.sort((a: any, b: any) => a.startMinutes - b.startMinutes);

        for (let index = 0; index < comparableRanges.length - 1; index += 1) {
            const current = comparableRanges[index];
            const next = comparableRanges[index + 1];
            if (current.endMinutes > next.startMinutes) {
                invalidIds.add(current.id);
                invalidIds.add(next.id);
            }
        }

        if (comparableRanges.length > 1 && comparableRanges.some((range: any) => invalidIds.has(range.id))) {
            messages.push('Time spans cannot overlap. Please adjust the highlighted ranges.');
        }

        if (comparableRanges.length > 0) {
            const earliestStart = comparableRanges[0].startMinutes;
            const latestEnd = comparableRanges[comparableRanges.length - 1].endMinutes;
            if (latestEnd - earliestStart > 24 * 60) {
                comparableRanges.forEach((range: any) => invalidIds.add(range.id));
                messages.push('The earliest start time and latest end time must stay within 24 hours.');
            }
        }

        return {
            invalidIds,
            message: messages[0] || '',
        };
    }, [ranges]);

    const handleRangeChange = (rangeId: any, field: any, value: any) => {
        setRanges((prev: any) =>
            prev.map((range: any) =>
                range.id === rangeId
                    ? {
                          ...range,
                          [field]: value,
                      }
                    : range
            )
        );
    };

    const handleAddRange = () => {
        setRanges((prev: any) => {
            if (prev.length >= MAX_RANGES) {
                return prev;
            }
            return [...prev, createRange(null, null)];
        });
    };

    const handleRemoveRange = (rangeId: any) => {
        setRanges((prev: any) => {
            if (prev.length === 1) {
                return prev;
            }
            return prev.filter((range: any) => range.id !== rangeId);
        });
    };

    return (
        <div className='workingTimeRanges'>
            {validation.message ? <div className='workingTimeRanges__error'>{validation.message}</div> : null}
            <div className='workingTimeRanges__list'>
                {ranges.map((range: any, index: any) => {
                    const hasError = validation.invalidIds.has(range.id);
                    const isLastRow = index === ranges.length - 1;

                    return (
                        <div
                            key={range.id}
                            className={`workingTimeRanges__row${hasError ? ' workingTimeRanges__row--error' : ''}${isLastRow ? ' workingTimeRanges__row--last' : ''}`}
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                    disabled={disabled}
                                    value={range.start}
                                    onChange={(newTime: any) => handleRangeChange(range.id, 'start', newTime)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            className: 'workingTimeRanges__field',
                                            sx: {
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '14px',
                                                    backgroundColor: '#F7F8FB',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: hasError ? '#E53935' : '#D9E1EC',
                                                    borderWidth: hasError ? '2px' : '1px',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <span className='workingTimeRanges__separator'>to</span>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                    disabled={disabled}
                                    value={range.end}
                                    onChange={(newTime: any) => handleRangeChange(range.id, 'end', newTime)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            className: 'workingTimeRanges__field',
                                            sx: {
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '14px',
                                                    backgroundColor: '#F7F8FB',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: hasError ? '#E53935' : '#D9E1EC',
                                                    borderWidth: hasError ? '2px' : '1px',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            {canAddMultipleRanges && (
                                <button
                                    type='button'
                                    className='workingTimeRanges__iconButton'
                                    onClick={() => handleRemoveRange(range.id)}
                                    disabled={disabled || ranges.length === 1}
                                    aria-label={`Remove time span ${index + 1}`}
                                >
                                    <RemoveIcon fontSize='small' />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {canAddMultipleRanges && (
                <button
                    type='button'
                    className='workingTimeRanges__addButton'
                    onClick={handleAddRange}
                    disabled={disabled || ranges.length >= MAX_RANGES}
                    aria-label='Add time span'
                >
                    <AddIcon fontSize='small' />
                </button>
            )}
            {offsetString ? <div className='workingTimeRanges__offset'>{offsetString}</div> : null}
        </div>
    );
}

export default WorkingTimeRangesEditor;
